"use client";

// Data layer Pendaftaran Mitra/Reseller (mig 3810). Logika murninya di
// src/lib/mitra.ts; modul ini hanya jembatan RPC/SELECT.

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { withTimeout } from "@/lib/withTimeout";
import {
  mapMitraOptions,
  mapMitraStats,
  type MitraOptions,
  type MitraStats,
} from "@/lib/mitra";
import type { DateRange } from "@/lib/reports/types";

/** Opsi pendaftaran utk pelanggan login (RPC `reseller_registration_options`). */
export async function fetchMitraOptions(): Promise<MitraOptions | null> {
  // Tanpa scope eksplisit (idiom TopupClient/addresses.ts): halaman /mitra =
  // storefront, auto-deteksi memilih sesi store; RPC-nya sendiri ter-scope
  // toko lewat current_store_id() dari JWT — tak ada query anon di sini.
  const db = createBrowserSupabase();
  if (!db) return null;
  const { data, error } = await withTimeout(
    db.rpc("reseller_registration_options")
  );
  if (error) throw new Error(error.message);
  return mapMitraOptions(data);
}

export type ApplyResellerUpgradeInput = {
  groupId: string;
  /** null utk level gratis. */
  amount: number | null;
  /** null = Tunai. */
  paymentAccountId: string | null;
  methodLabel: string | null;
  /** Storage PATH bukti di bucket payment-proofs (bukan URL). */
  proofPath: string | null;
};

export type ApplyResellerUpgradeResult = {
  applicationId: string | null;
  status: "approved" | "pending";
  free: boolean;
  topupRequestId: string | null;
};

/** Ajukan pendaftaran / naik level (RPC `apply_reseller_upgrade`). */
export async function applyResellerUpgrade(
  input: ApplyResellerUpgradeInput
): Promise<ApplyResellerUpgradeResult> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Client tidak tersedia.");
  const { data, error } = await withTimeout(
    db.rpc("apply_reseller_upgrade", {
      p_group_id: input.groupId,
      p_amount: input.amount,
      p_payment_account_id: input.paymentAccountId,
      p_method_label: input.methodLabel,
      p_proof_url: input.proofPath,
    })
  );
  if (error) throw new Error(error.message);
  const raw = (data ?? {}) as Record<string, unknown>;
  return {
    applicationId:
      typeof raw.applicationId === "string" ? raw.applicationId : null,
    status: raw.status === "approved" ? "approved" : "pending",
    free: raw.free === true,
    topupRequestId:
      typeof raw.topupRequestId === "string" ? raw.topupRequestId : null,
  };
}

export type MyApprovedApplication = {
  id: string;
  targetGroupName: string;
  decidedAt: string | null;
};

/**
 * Pengajuan mitra TERAKHIR yang disetujui milik pelanggan login (RLS select
 * own) — bahan popup "Selamat jadi mitra" satu-kali. Gagal → null (popup tak
 * tampil; ia perayaan, bukan gerbang).
 */
export async function fetchMyLatestApprovedApplication(): Promise<MyApprovedApplication | null> {
  const db = createBrowserSupabase();
  if (!db) return null;
  try {
    const { data, error } = await withTimeout(
      db
        .from("reseller_applications")
        .select("id, target_group_name, decided_at")
        .eq("status", "approved")
        .order("decided_at", { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle()
    );
    if (error || !data) return null;
    const row = data as {
      id: string;
      target_group_name: string | null;
      decided_at: string | null;
    };
    return {
      id: row.id,
      targetGroupName: row.target_group_name ?? "Mitra",
      decidedAt: row.decided_at ?? null,
    };
  } catch {
    return null;
  }
}

// ── Sisi admin ──────────────────────────────────────────────────────────────

export type AdminMitraApplication = {
  id: string;
  status: string;
  targetGroupName: string;
  targetGroupSlot: number;
  minTopup: number;
  isFree: boolean;
  note: string | null;
  createdAt: string;
  decidedAt: string | null;
  customerName: string;
  customerPhone: string;
};

type AdminApplicationRow = {
  id: string;
  status: string;
  target_group_name: string;
  target_group_slot: number | string | null;
  min_topup: number | string | null;
  is_free: boolean;
  note: string | null;
  created_at: string;
  decided_at: string | null;
  customers: { full_name: string | null; phone: string | null } | null;
};

/**
 * Daftar pengajuan mitra terbaru — kartu "Pengajuan Mitra" di
 * /admin/reseller-groups (RLS admin; sesi admin terdeteksi dari URL /admin).
 */
export async function fetchRecentMitraApplications(
  limit = 20
): Promise<AdminMitraApplication[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const { data, error } = await withTimeout(
    db
      .from("reseller_applications")
      .select(
        "id, status, target_group_name, target_group_slot, min_topup, is_free, note, created_at, decided_at, customers(full_name, phone)"
      )
      .order("created_at", { ascending: false })
      .limit(limit)
  );
  if (error) throw new Error(error.message);
  return ((data as unknown as AdminApplicationRow[] | null) ?? []).map((r) => ({
    id: r.id,
    status: r.status,
    targetGroupName: r.target_group_name,
    targetGroupSlot: Math.trunc(Number(r.target_group_slot) || 0),
    minTopup: Math.max(0, Math.trunc(Number(r.min_topup) || 0)),
    isFree: r.is_free === true,
    note: r.note ?? null,
    createdAt: r.created_at,
    decidedAt: r.decided_at ?? null,
    customerName: r.customers?.full_name ?? "—",
    customerPhone: r.customers?.phone ?? "—",
  }));
}

/**
 * Statistik konversi mitra utk dasbor SUPER ADMIN (RPC `admin_mitra_stats`,
 * mig 3820 — RPC TERPISAH dari admin_dashboard_today). Rentang WAJIB
 * (cermin dashRange).
 */
export async function fetchMitraStats(
  range: DateRange
): Promise<MitraStats | null> {
  const db = createBrowserSupabase();
  if (!db) return null;
  const { data, error } = await withTimeout(
    db.rpc("admin_mitra_stats", { p_start: range.start, p_end: range.end })
  );
  if (error) throw new Error(error.message);
  return mapMitraStats(data);
}

/**
 * Peta topup_request_id → nama level utk chip "Pendaftaran Mitra" di tab
 * Top-up Pending (/admin/saldo). Gagal = peta kosong (chip hilang, daftar
 * top-up tetap hidup — chip hanya penjelas, bukan gerbang).
 */
export async function fetchMitraApplicationsByTopupIds(
  topupIds: string[]
): Promise<Record<string, { levelName: string; minTopup: number }>> {
  if (!topupIds.length) return {};
  const db = createBrowserSupabase();
  if (!db) return {};
  try {
    const { data, error } = await withTimeout(
      db
        .from("reseller_applications")
        .select("topup_request_id, target_group_name, min_topup")
        .eq("status", "pending")
        .in("topup_request_id", topupIds)
    );
    if (error) return {};
    const map: Record<string, { levelName: string; minTopup: number }> = {};
    for (const row of (data ?? []) as Array<{
      topup_request_id: string | null;
      target_group_name: string | null;
      min_topup: number | string | null;
    }>) {
      if (!row.topup_request_id) continue;
      map[row.topup_request_id] = {
        levelName: row.target_group_name ?? "Mitra",
        minTopup: Math.max(0, Math.trunc(Number(row.min_topup) || 0)),
      };
    }
    return map;
  } catch {
    return {};
  }
}
